import { getDefaultShipping, getUnexpiredCards } from 'utils';

import actionTypes from '../actions/actionTypes';
import { sortPaymentMethodsByDefaultCard, mapLoyaltyCards, updateLoyaltyCardBalance } from '../utils';

const INITIAL_STATE = {
  clientAccountDetails: {},
  selectedShippingAddress: {},
  giftCards: [],
  updatePaymentsLoading: false,
  updatePaymentsError: null,
  addGiftCardError: null,
  clientAccountDetailsError: null,
  clientAccountDetailsLoading: true,
};

const selectPayments = (payments, amountsMap) => {
  return payments.map((paymentMethod) => {
    return {
      ...paymentMethod,
      selected: !!amountsMap[paymentMethod.keyIdentifier],
      amount: amountsMap[paymentMethod.keyIdentifier] || 0,
    };
  });
};

const selectGiftCards = (giftCards, amountsMap) => {
  return giftCards.map((giftCard) => {
    return {
      ...giftCard,
      selected: !!amountsMap[giftCard.giftCardNumber],
      selectedBalance: amountsMap[giftCard.giftCardNumber] || 0,
    };
  });
};

// After successful payment we must sum all previous used card amounts to show right data in orderConfirmation page
const sumPaymentsAmounts = (payments, amountsMap) => {
  return payments.map((paymentMethod) => {
    const submittedAmount = amountsMap[paymentMethod.keyIdentifier] || 0;
    const sum = Number(paymentMethod.amount || 0) + Number(submittedAmount);

    return {
      ...paymentMethod,
      amount: sum,
      // mark all used payment methods as selected
      selected: !!sum,
    };
  });
};

const sumGiftCardsAmounts = (giftCards, amountsMap) => {
  return giftCards.map((giftCard) => {
    const submittedAmount = amountsMap[giftCard.giftCardNumber] || 0;
    const sum = Number(giftCard.selectedBalance || 0) + Number(submittedAmount);

    return {
      ...giftCard,
      selectedBalance: sum,
      // mark all gift cards as selected
      selected: !!sum,
    };
  });
};

// Here we mark used gift cards as hidden and unselect all gift cards after failed payment
const updateGiftCardsAfterFailedPayment = (giftCards, selectedGiftCards, failedGiftCardNumbers) => {
  const selectedGiftCardsNumbers = selectedGiftCards.map(({ giftCardNumber }) => giftCardNumber);

  return giftCards.map((giftCard) => {
    const isFailed = failedGiftCardNumbers.includes(giftCard.giftCardNumber);
    const isAllBalanceUsed =
      selectedGiftCardsNumbers.includes(giftCard.giftCardNumber) &&
      giftCard.giftCardBalance - giftCard.selectedBalance < 1;

    return {
      ...giftCard,
      isHidden: isFailed || isAllBalanceUsed,
      selected: false,
    };
  });
};

const accountDetails = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case actionTypes.ACCOUNT_DETAILS_LOADING:
      return { ...INITIAL_STATE, clientAccountDetailsLoading: true };

    case actionTypes.ACCOUNT_DETAILS_SUCCESS:
      return {
        ...state,
        clientAccountDetails: {
          ...payload,
          payments: sortPaymentMethodsByDefaultCard(payload.payments),
        },
        // selectedShippingAddress cannot be empty
        selectedShippingAddress: getDefaultShipping(payload.shippingAddress) || {},
        clientAccountDetailsLoading: false,
      };

    case actionTypes.ACCOUNT_DETAILS_ERROR:
      return { ...state, clientAccountDetailsLoading: false, clientAccountDetailsError: payload };

    case actionTypes.ACCOUNT_DETAILS_SELECT_SHIPPING_ADDRESS:
      return {
        ...state,
        selectedShippingAddress: payload,
      };

    case actionTypes.ACCOUNT_DETAILS_UPDATE_PAYMENTS_LOADING:
      return { ...state, updatePaymentsLoading: payload };

    case actionTypes.ACCOUNT_DETAILS_UPDATE_PAYMENTS_ERROR:
      return { ...state, updatePaymentsLoading: false, updatePaymentsError: payload };

    case actionTypes.ACCOUNT_DETAILS_UPDATE_PAYMENTS_ERROR_RESET:
      return { ...state, updatePaymentsError: null };

    case actionTypes.ACCOUNT_DETAILS_SET_SUBMITTED_AMOUNTS:
      return {
        ...state,
        clientAccountDetails: {
          ...state.clientAccountDetails,
          payments: sumPaymentsAmounts(state.clientAccountDetails.payments, payload.submittedPaymentsAmountMap),
        },
        giftCards: sumGiftCardsAmounts(state.giftCards, payload.submittedGiftCardAmountMap),
      };

    case actionTypes.ACCOUNT_DETAILS_UPDATE_PAYMENTS_SUCCESS:
      return {
        ...state,
        clientAccountDetails: {
          ...state.clientAccountDetails,
          payments: sortPaymentMethodsByDefaultCard(payload),
        },
        updatePaymentsLoading: false,
      };

    case actionTypes.ACCOUNT_DETAILS_SELECT_PAYMENTS:
      return {
        ...state,
        clientAccountDetails: {
          ...state.clientAccountDetails,
          payments: selectPayments(state.clientAccountDetails.payments, payload),
        },
      };

    case actionTypes.ACCOUNT_DETAILS_ADD_LOYALITY_GIFT_CARDS:
      return {
        ...state,
        giftCards: getUnexpiredCards(mapLoyaltyCards(payload, state.giftCards)),
      };

    case actionTypes.ACCOUNT_DETAILS_CHECK_LOYALTY_CARD_BALANCE:
      return {
        ...state,
        giftCards: updateLoyaltyCardBalance(payload, state.giftCards),
      };

    case actionTypes.ACCOUNT_DETAILS_SELECT_GIFT_CARDS:
      return {
        ...state,
        giftCards: selectGiftCards(state.giftCards, payload),
      };

    case actionTypes.ACCOUNT_DETAILS_ADD_GIFT_CARD:
      return {
        ...state,
        giftCards: [...state.giftCards, payload],
      };

    case actionTypes.ACCOUNT_DETAILS_ADD_GIFT_CARD_ERROR:
      return { ...state, addGiftCardError: payload };

    case actionTypes.ACCOUNT_DETAILS_UPDATE_GIFT_CARDS_AFTER_FAILED_PAYMENT:
      return {
        ...state,
        giftCards: updateGiftCardsAfterFailedPayment(
          state.giftCards,
          payload.selectedGiftCards,
          payload.failedGiftCardNumbers,
        ),
      };

    case actionTypes.ACCOUNT_DETAILS_RESET:
      return { ...INITIAL_STATE };

    default:
      return state;
  }
};

export default accountDetails;
